import { create } from 'zustand';
import type { Model3D, Marker, SubObjectConfig } from '@dtwin/shared';

interface Snapshot {
  models: Model3D[];
  markers: Marker[];
  /** 2026-09-14：组合模型小部件业务配置（软删除/重命名等部件操作撤销用） */
  subObjectConfigs?: Record<string, SubObjectConfig[]>;
  // ── E0（2026-10-07）：扩展文档态（命令总线事务快照用） ──
  // 旧快照缺省这些字段 = 不恢复（保持既有行为）；总线快照携带 → undo/redo 一并恢复
  environment?: unknown;
  effects?: unknown;
  lights?: unknown[];
  displayRegion?: unknown;
  showGrid?: boolean;
  showGround?: boolean;
  cameraStrategy?: unknown;
  savedCamera?: unknown;
}

interface HistoryState {
  past: Snapshot[];
  future: Snapshot[];
  canUndo: boolean;
  canRedo: boolean;
  pushSnapshot: (snapshot: Snapshot) => void;
  /**
   * 撤销：应用 past 栈顶快照；并把「当前状态」压入 future（供 redo 还原）。
   * 2026-09-27（审计 FE-C03 修复）：原实现把 past 快照原样压入 future，redo 重放同一
   * 「变更前」快照 → 相对当前状态无变化（重做无效）。改为由调用方传入当前状态。
   */
  undo: (current?: Snapshot) => Snapshot | null;
  /** 重做：应用 future 栈顶快照；并把「当前状态」压回 past（供再次 undo） */
  redo: (current?: Snapshot) => Snapshot | null;
  clear: () => void;
}

const MAX_HISTORY = 50;

const useHistoryStore = create<HistoryState>((set, get) => ({
  past: [],
  future: [],
  canUndo: false,
  canRedo: false,

  pushSnapshot: (snapshot) => {
    set((state) => {
      const past = [...state.past, snapshot].slice(-MAX_HISTORY);
      return { past, future: [], canUndo: true, canRedo: false };
    });
  },

  undo: (current) => {
    const { past } = get();
    if (past.length === 0) return null;
    const snapshot = past[past.length - 1];
    set((state) => ({
      past: state.past.slice(0, -1),
      future: [...state.future, current ?? snapshot],
      canUndo: state.past.length > 1,
      canRedo: true,
    }));
    return snapshot;
  },

  redo: (current) => {
    const { future } = get();
    if (future.length === 0) return null;
    const snapshot = future[future.length - 1];
    set((state) => ({
      future: state.future.slice(0, -1),
      past: [...state.past, current ?? snapshot],
      canUndo: true,
      canRedo: state.future.length > 1,
    }));
    return snapshot;
  },

  clear: () => set({ past: [], future: [], canUndo: false, canRedo: false }),
}));

export default useHistoryStore;
